/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';

// ── Types ──────────────────────────────────────────────────────────────
interface Question { text: string; optionA: string; optionB: string; correct: 'A' | 'B'; }
interface Result { question: Question; chosen: 'A' | 'B' | null; isCorrect: boolean; }
type GamePhase = 'input' | 'playing' | 'result';
type FeedbackType = 'correct' | 'wrong' | null;
type Zone = 'A' | 'B' | null;

// ── Animations ─────────────────────────────────────────────────────────
const fadeIn = keyframes`from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}`;
const pulse = keyframes`0%,100%{transform:scale(1)}50%{transform:scale(1.15)}`;
const progressAnim = keyframes`from{width:0%}to{width:100%}`;

// ── Styled Components ──────────────────────────────────────────────────
const Wrapper = styled.div`min-height:100vh;background:linear-gradient(135deg,#0f0c29,#302b63,#24243e);color:white;font-family:'Segoe UI',sans-serif;`;
const InputScreen = styled.div`max-width:700px;margin:0 auto;padding:50px 24px;animation:${fadeIn} .5s ease;`;
const Title = styled.h1`text-align:center;font-size:2.4rem;font-weight:800;background:linear-gradient(90deg,#60a5fa,#a78bfa,#f472b6);-webkit-background-clip:text;-webkit-text-fill-color:transparent;margin-bottom:6px;`;
const Sub = styled.p`text-align:center;color:rgba(255,255,255,.55);margin-bottom:28px;font-size:.9rem;`;
const FormatHint = styled.div`background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.12);border-radius:10px;padding:12px 16px;margin-bottom:16px;font-size:.82rem;color:rgba(255,255,255,.65);pre{margin:4px 0 0;color:#93c5fd;font-size:.79rem;}`;
const TextArea = styled.textarea`width:100%;height:280px;background:rgba(255,255,255,.07);border:1.5px solid rgba(255,255,255,.2);border-radius:12px;color:white;padding:14px 16px;font-size:.93rem;resize:vertical;outline:none;transition:border-color .2s;&:focus{border-color:#818cf8;}&::placeholder{color:rgba(255,255,255,.3);}`;
const PlayBtn = styled.button<{ disabled?: boolean }>`width:100%;margin-top:16px;padding:15px;border:none;border-radius:14px;font-size:1.05rem;font-weight:700;cursor:${p => p.disabled ? 'not-allowed' : 'pointer'};background:${p => p.disabled ? 'rgba(255,255,255,.1)' : 'linear-gradient(135deg,#6366f1,#8b5cf6)'};color:white;opacity:${p => p.disabled ? .5 : 1};transition:all .2s;&:hover:not(:disabled){transform:translateY(-2px);box-shadow:0 8px 24px rgba(99,102,241,.35);}`;

// Game layout
const GameOuter = styled.div`display:flex;flex-direction:column;height:100vh;overflow:hidden;`;
const QuestionBanner = styled.div`
  width:100%;padding:18px 40px 14px;
  background:rgba(0,0,0,.5);
  border-bottom:1px solid rgba(255,255,255,.08);
  display:flex;align-items:center;justify-content:space-between;gap:20px;
  flex-shrink:0;
`;
const QBannerText = styled.div`
  font-size:3.3rem;font-weight:900;line-height:1.25;
  color:white;flex:1;text-align:center;
  white-space:normal;overflow:visible;
`;
const TimerDisplay = styled.div`
  font-size:1.6rem;font-weight:700;font-variant-numeric:tabular-nums;
  color:#a78bfa;white-space:nowrap;min-width:90px;text-align:right;
`;
const GameLayout = styled.div`display:flex;flex:1;overflow:hidden;`;
const AnswerZone = styled.div<{ side: 'A' | 'B'; active: boolean; fbType: FeedbackType }>`
  flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:20px;
  position:relative;
  background:${p => p.fbType === 'correct' ? 'rgba(34,197,94,.28)' : p.fbType === 'wrong' ? 'rgba(239,68,68,.28)' : p.active ? (p.side === 'A' ? 'rgba(59,130,246,.22)' : 'rgba(168,85,247,.22)') : 'rgba(255,255,255,.03)'};
  border-right:${p => p.side === 'A' ? '2px solid rgba(255,255,255,.08)' : 'none'};
  border-left:${p => p.side === 'B' ? '2px solid rgba(255,255,255,.08)' : 'none'};
  transition:background .25s;
`;
const AnswerLabel = styled.div<{ side: 'A' | 'B' }>`
  position:absolute;top:20px;
  font-size:1.4rem;font-weight:900;
  letter-spacing:.1em;
  color:${p => p.side === 'A' ? '#60a5fa' : '#c084fc'};
  opacity:.8;
  text-transform:uppercase;
`;
const AnswerText = styled.div<{ side: 'A' | 'B' }>`
  font-size:2.8rem;font-weight:900;text-align:center;line-height:1.3;
  background:${p => p.side === 'A'
        ? 'linear-gradient(135deg,#60a5fa,#38bdf8)'
        : 'linear-gradient(135deg,#c084fc,#e879f9)'};
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;
  text-shadow:none;
  padding:0 12px;
`;
const CenterPanel = styled.div`width:360px;flex-shrink:0;display:flex;flex-direction:column;align-items:center;padding:16px 12px;background:rgba(0,0,0,.35);overflow:hidden;`;
const QCounter = styled.div`font-size:.8rem;color:rgba(255,255,255,.45);margin-bottom:8px;`;
const QText = styled.div`font-size:1.65rem;font-weight:700;text-align:center;line-height:1.5;margin-bottom:12px;color:white;min-height:56px;`;
const VideoBox = styled.div`position:relative;width:100%;border-radius:12px;overflow:hidden;background:#000;`;
const VideoEl = styled.video`width:100%;display:block;transform:scaleX(-1);`;
const CanvasEl = styled.canvas`position:absolute;top:0;left:0;width:100%;height:100%;transform:scaleX(-1);pointer-events:none;`;
const ZoneLabel = styled.div<{ side: 'A' | 'B'; active: boolean }>`
  position:absolute;top:50%;transform:translateY(-50%);${p => p.side === 'A' ? 'left:8px' : 'right:8px'};
  font-size:2rem;font-weight:900;color:${p => p.side === 'A' ? '#60a5fa' : '#c084fc'};
  opacity:${p => p.active ? .95 : .4};transition:opacity .2s;
  text-shadow:0 0 12px ${p => p.side === 'A' ? '#60a5fa' : '#c084fc'};
`;
const HoldBarWrap = styled.div`position:absolute;bottom:0;left:0;right:0;height:5px;background:rgba(255,255,255,.1);`;
const HoldBarFill = styled.div<{ side: 'A' | 'B'; dur: number; active: boolean }>`
  height:100%;background:${p => p.side === 'A' ? '#60a5fa' : '#c084fc'};
  animation:${p => p.active ? css`${progressAnim} ${p.dur}ms linear forwards` : 'none'};
`;
const FbOverlay = styled.div<{ type: FeedbackType }>`
  position:fixed;inset:0;display:flex;align-items:center;justify-content:center;
  font-size:6rem;pointer-events:none;z-index:99;
  animation:${p => p.type ? css`${pulse} .5s ease` : 'none'};
  opacity:${p => p.type ? 1 : 0};transition:opacity .3s;
`;
const StatusMsg = styled.div`font-size:.75rem;color:rgba(255,255,255,.45);text-align:center;margin:6px 0 4px;`;
const ManualBtns = styled.div`display:flex;gap:8px;margin-top:4px;width:100%;`;
const ManualBtn = styled.button<{ side: 'A' | 'B' }>`
  flex:1;padding:8px;border:1px solid ${p => p.side === 'A' ? '#60a5fa' : '#c084fc'};border-radius:8px;
  background:transparent;color:${p => p.side === 'A' ? '#60a5fa' : '#c084fc'};cursor:pointer;font-size:.8rem;font-weight:600;
  &:hover{background:${p => p.side === 'A' ? 'rgba(96,165,250,.15)' : 'rgba(192,132,252,.15)'};}  
`;
const EndBtn = styled.button`
  width:100%;margin-top:8px;padding:7px;
  border:1px solid rgba(248,113,113,.5);border-radius:8px;
  background:transparent;color:rgba(248,113,113,.8);cursor:pointer;
  font-size:.8rem;font-weight:600;transition:all .2s;
  &:hover{background:rgba(248,113,113,.12);border-color:#f87171;color:#f87171;}
`;
// Result
const ResultScreen = styled.div`max-width:800px;margin:0 auto;padding:50px 24px;animation:${fadeIn} .5s ease;`;
const ScoreCard = styled.div`text-align:center;margin-bottom:32px;`;
const ResultItem = styled.div<{ correct: boolean }>`
  background:${p => p.correct ? 'rgba(34,197,94,.1)' : 'rgba(239,68,68,.1)'};
  border:1.5px solid ${p => p.correct ? 'rgba(34,197,94,.3)' : 'rgba(239,68,68,.3)'};
  border-radius:12px;padding:14px 18px;margin-bottom:10px;animation:${fadeIn} .3s ease;
`;
const BackBtn = styled.button`margin-top:24px;padding:13px 32px;border:none;border-radius:12px;background:linear-gradient(135deg,#6366f1,#8b5cf6);color:white;font-size:1rem;font-weight:700;cursor:pointer;transition:all .2s;&:hover{transform:translateY(-2px);}`;

// ── Constants ──────────────────────────────────────────────────────────
const HOLD_MS = 1500;
const MOTION_THRESHOLD = 8;    // pixel diff to consider moved (lower = more sensitive)
const ZONE_RATIO = 0.40;       // left/right 40% of frame = detection zone
const MOTION_MIN = 60;         // min motion pixels to register presence
const FRAME_SKIP = 6;          // compare every Nth frame (bigger diff = clearer signal)

// ── Parse Questions ────────────────────────────────────────────────────
function parseQuestions(raw: string): Question[] {
    const blocks = raw.trim().split(/\n\s*\n+/).filter(Boolean);
    const out: Question[] = [];
    for (const block of blocks) {
        const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
        if (lines.length < 3) continue;
        const text = lines[0].replace(/^\d+[\.\)]\s*/, '');
        let optA = '', optB = '', correct: 'A' | 'B' = 'A';
        for (const line of lines.slice(1)) {
            const isCorrect = line.startsWith('*');
            const clean = line.replace(/^\*/, '');
            if (/^A[\.\):\s]/i.test(clean)) { optA = clean.replace(/^A[\.\):\s]/i, '').trim(); if (isCorrect) correct = 'A'; }
            else if (/^B[\.\):\s]/i.test(clean)) { optB = clean.replace(/^B[\.\):\s]/i, '').trim(); if (isCorrect) correct = 'B'; }
        }
        if (text && optA && optB) out.push({ text, optionA: optA, optionB: optB, correct });
    }
    return out;
}

// ── Sound Effects ──────────────────────────────────────────────────────
function beep(freq: number, type: OscillatorType, dur: number, delay = 0, vol = 0.3) {
    try {
        const c = new (window.AudioContext || (window as any).webkitAudioContext)();
        const o = c.createOscillator(), g = c.createGain();
        o.connect(g); g.connect(c.destination); o.type = type;
        o.frequency.setValueAtTime(freq, c.currentTime + delay);
        g.gain.setValueAtTime(vol, c.currentTime + delay);
        g.gain.exponentialRampToValueAtTime(.001, c.currentTime + delay + dur);
        o.start(c.currentTime + delay); o.stop(c.currentTime + delay + dur);
    } catch { }
}
const playCorrect = () => {
    beep(523, 'sine', .12, 0, .3);
    beep(659, 'sine', .12, .10, .3);
    beep(784, 'sine', .12, .20, .3);
    beep(1046, 'sine', .4, .30, .4);
};
const playWrong = () => {
    beep(350, 'square', .15, 0, .25);
    beep(280, 'square', .15, .13, .25);
    beep(200, 'sawtooth', .3, .26, .2);
};

// ── Background Music ───────────────────────────────────────────────────
// Fun pentatonic melody: C5-E5-G5-A5-C6 pattern + bass line, loops forever
// Volume 0.07 — much quieter than answer SFX (0.25-0.4)
const BG_NOTES = [523, 659, 784, 880, 1047, 880, 784, 659, 523, 587, 659, 784, 523, 659, 0, 0];
const BG_DUR = [0.12, 0.12, 0.12, 0.12, 0.18, 0.12, 0.12, 0.12, 0.12, 0.12, 0.12, 0.12, 0.18, 0.18, 0.12, 0.12];
let bgCtx: AudioContext | null = null;
let bgGain: GainNode | null = null;
let bgStopFlag = false;

function startBgMusic() {
    bgStopFlag = false;
    try {
        bgCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        bgGain = bgCtx.createGain();
        bgGain.gain.setValueAtTime(0.15, bgCtx.currentTime);
        bgGain.connect(bgCtx.destination);
    } catch { return; }

    let noteIdx = 0;
    let t = bgCtx.currentTime + 0.05;

    function scheduleNext() {
        if (bgStopFlag || !bgCtx || !bgGain) return;
        const freq = BG_NOTES[noteIdx % BG_NOTES.length];
        const dur = BG_DUR[noteIdx % BG_DUR.length];
        noteIdx++;
        if (freq > 0) {
            const o = bgCtx.createOscillator();
            const g = bgCtx.createGain();
            o.connect(g); g.connect(bgGain);
            o.type = 'triangle';
            o.frequency.setValueAtTime(freq, t);
            g.gain.setValueAtTime(0.15, t);
            g.gain.exponentialRampToValueAtTime(0.001, t + dur * 0.85);
            o.start(t); o.stop(t + dur);
        }
        t += dur;
        // Schedule ahead using setTimeout so we don't pre-calculate the whole loop
        const msUntilNext = Math.max(0, (t - bgCtx.currentTime - 0.1) * 1000);
        setTimeout(scheduleNext, msUntilNext);
    }
    scheduleNext();
}

function stopBgMusic() {
    bgStopFlag = true;
    try { bgGain?.gain.setValueAtTime(0, bgCtx?.currentTime ?? 0); } catch { }
    try { bgCtx?.close(); } catch { }
    bgCtx = null; bgGain = null;
}

// ── Main Component ─────────────────────────────────────────────────────
const ClapGame: React.FC = () => {
    const [phase, setPhase] = useState<GamePhase>('input');
    const [rawText, setRawText] = useState('');
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentIdx, setCurrentIdx] = useState(0);
    const [results, setResults] = useState<Result[]>([]);
    const [feedback, setFeedback] = useState<FeedbackType>(null);
    const [activeZone, setActiveZone] = useState<Zone>(null);
    const [holdInfo, setHoldInfo] = useState<{ zone: Zone; startedAt: number } | null>(null);
    const [camError, setCamError] = useState('');
    const [debugMotion, setDebugMotion] = useState({ left: 0, right: 0 });
    const [elapsed, setElapsed] = useState(0);      // ms since game start
    const [totalTime, setTotalTime] = useState(0);  // final time in ms

    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const offCanvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'));
    const prevDataRef = useRef<ImageData | null>(null);
    const rafRef = useRef<number>(0);
    const holdRef = useRef<{ zone: Zone; startedAt: number } | null>(null);
    const feedbackLock = useRef(false);
    const streamRef = useRef<MediaStream | null>(null);
    const frameCountRef = useRef(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const gameStartRef = useRef(0);
    // Refs that mirror state — allow reading current values inside callbacks without stale closures
    const questionsRef = useRef<Question[]>([]);
    const currentIdxRef = useRef(0);
    const resultsRef = useRef<Result[]>([]);

    // ── Motion detection loop ──────────────────────────────────────────
    const detect = useCallback(() => {
        const video = videoRef.current;
        const offCanvas = offCanvasRef.current;
        rafRef.current = requestAnimationFrame(detect);

        // Skip frames so compared frames are further apart → bigger motion delta
        frameCountRef.current++;
        if (frameCountRef.current % FRAME_SKIP !== 0) return;

        if (!video || video.readyState < 2 || feedbackLock.current) return;
        const W = video.videoWidth, H = video.videoHeight;
        if (!W || !H) return;
        offCanvas.width = W; offCanvas.height = H;
        const ctx = offCanvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;

        ctx.drawImage(video, 0, 0, W, H);
        const curr = ctx.getImageData(0, 0, W, H);
        const prev = prevDataRef.current;

        // Draw overlay on visible canvas
        const visCanvas = canvasRef.current;
        if (visCanvas) {
            visCanvas.width = W; visCanvas.height = H;
            const vCtx = visCanvas.getContext('2d');
            if (vCtx) {
                vCtx.clearRect(0, 0, W, H);
                vCtx.strokeStyle = 'rgba(255,255,255,0.3)';
                vCtx.lineWidth = 2;
                vCtx.setLineDash([6, 4]);
                vCtx.beginPath(); vCtx.moveTo(W * ZONE_RATIO, 0); vCtx.lineTo(W * ZONE_RATIO, H); vCtx.stroke();
                vCtx.beginPath(); vCtx.moveTo(W * (1 - ZONE_RATIO), 0); vCtx.lineTo(W * (1 - ZONE_RATIO), H); vCtx.stroke();
                vCtx.setLineDash([]);
            }
        }

        if (prev) {
            let leftMotion = 0, rightMotion = 0;
            const d = curr.data, p = prev.data;
            // Sample every 4th pixel for performance
            for (let i = 0; i < d.length; i += 16) {
                const pixIdx = i / 4;
                const px = pixIdx % W;
                const diff = (Math.abs(d[i] - p[i]) + Math.abs(d[i + 1] - p[i + 1]) + Math.abs(d[i + 2] - p[i + 2])) / 3;
                if (diff > MOTION_THRESHOLD) {
                    // Video CSS is scaleX(-1), raw pixels map: low px = screen right, high px = screen left
                    if (px > W * (1 - ZONE_RATIO)) leftMotion++;   // raw right → screen left → zone A
                    else if (px < W * ZONE_RATIO) rightMotion++;   // raw left → screen right → zone B
                }
            }
            setDebugMotion({ left: leftMotion, right: rightMotion });

            // Determine active zone — whichever side has more motion above minimum
            let zone: Zone = null;
            if (leftMotion > MOTION_MIN || rightMotion > MOTION_MIN) {
                zone = leftMotion >= rightMotion ? 'A' : 'B';
            }
            setActiveZone(zone);

            const curHold = holdRef.current;
            if (zone) {
                if (curHold?.zone === zone) {
                    const elapsed = Date.now() - curHold.startedAt;
                    if (elapsed >= HOLD_MS) { triggerSelect(zone); }
                } else {
                    const nh = { zone, startedAt: Date.now() };
                    holdRef.current = nh;
                    setHoldInfo(nh);
                }
            } else {
                holdRef.current = null;
                setHoldInfo(null);
            }
        }
        prevDataRef.current = curr;
    }, []); // eslint-disable-line

    // ── Select answer — reads refs directly to avoid nested updater side effects ──
    const triggerSelect = useCallback((chosen: 'A' | 'B') => {
        if (feedbackLock.current) return;
        feedbackLock.current = true;
        holdRef.current = null;
        setHoldInfo(null);
        setActiveZone(null);

        const qs = questionsRef.current;
        const idx = currentIdxRef.current;
        const q = qs[idx];
        if (!q) { feedbackLock.current = false; return; }

        const isCorrect = chosen === q.correct;
        const newResult: Result = { question: q, chosen, isCorrect };
        const newResults = [...resultsRef.current, newResult];
        resultsRef.current = newResults;

        setResults(newResults);
        setFeedback(isCorrect ? 'correct' : 'wrong');
        if (isCorrect) playCorrect(); else playWrong();

        setTimeout(() => {
            setFeedback(null);
            feedbackLock.current = false;
            prevDataRef.current = null;
            if (idx + 1 >= qs.length) {
                setTotalTime(Date.now() - gameStartRef.current);
                stopCamera();
                setPhase('result');
            } else {
                currentIdxRef.current = idx + 1;
                setCurrentIdx(idx + 1);
            }
        }, 1700);
    }, []); // eslint-disable-line

    // ── Camera ─────────────────────────────────────────────────────────
    const startCamera = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 360, height: 270, facingMode: 'user' }, audio: false });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play();
            }
            rafRef.current = requestAnimationFrame(detect);
        } catch (e: any) {
            setCamError('Không thể truy cập camera: ' + (e.message || e));
        }
    }, [detect]);

    const stopCamera = () => {
        cancelAnimationFrame(rafRef.current);
        streamRef.current?.getTracks().forEach(t => t.stop());
        // stop timer and background music
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        stopBgMusic();
    };

    useEffect(() => {
        if (phase === 'playing') startCamera();
        return () => { if (phase === 'playing') stopCamera(); };
    }, [phase]); // eslint-disable-line

    useEffect(() => () => stopCamera(), []);

    // ── Play ────────────────────────────────────────────────────────────
    const handlePlay = () => {
        const parsed = parseQuestions(rawText);
        if (!parsed.length) return;
        // Fisher-Yates shuffle — randomize order every time
        const qs = [...parsed];
        for (let i = qs.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [qs[i], qs[j]] = [qs[j], qs[i]];
        }
        // Reset all state and refs
        questionsRef.current = qs;
        currentIdxRef.current = 0;
        resultsRef.current = [];
        setQuestions(qs); setCurrentIdx(0); setResults([]);
        setFeedback(null); feedbackLock.current = false;
        holdRef.current = null; setHoldInfo(null);
        prevDataRef.current = null;
        // Start stopwatch + background music
        setElapsed(0);
        gameStartRef.current = Date.now();
        timerRef.current = setInterval(() => {
            setElapsed(Date.now() - gameStartRef.current);
        }, 100);
        startBgMusic();
        setPhase('playing');
    };

    const parsedCount = rawText.trim() ? parseQuestions(rawText).length : 0;
    const example = `1. What color is the sky?\nA. Red\n*B. Blue\n\n2. Which says "meow"?\n*A. Cat\nB. Dog`;

    // ══════════════════════════════════════════════════════════════════
    // INPUT SCREEN
    // ══════════════════════════════════════════════════════════════════
    if (phase === 'input') return (
        <Wrapper>
            <InputScreen>
                <Title>👏 Trò Chơi Vẫy Tay</Title>
                <Sub>Vẫy tay về phía đáp án — camera nhận diện chuyển động, không cần ML hoặc WebGL</Sub>
                <FormatHint>
                    <strong>📋 Định dạng:</strong> Mỗi câu cách nhau 1 dòng trống. Đặt <code>*</code> trước đáp án đúng.
                    <pre>{`1. Câu hỏi?\nA. Lựa chọn\n*B. Đáp án đúng ← có dấu *`}</pre>
                </FormatHint>
                <TextArea placeholder={example} value={rawText} onChange={e => setRawText(e.target.value)} />
                {rawText.trim() && (
                    <div style={{ textAlign: 'center', color: parsedCount > 0 ? '#86efac' : '#fca5a5', marginTop: 8, fontSize: 13 }}>
                        {parsedCount > 0 ? `✅ ${parsedCount} câu hỏi` : '⚠️ Chưa nhận được câu nào — kiểm tra định dạng'}
                    </div>
                )}
                <PlayBtn disabled={parsedCount === 0} onClick={handlePlay}>🎮 Bắt Đầu ({parsedCount} câu)</PlayBtn>
            </InputScreen>
        </Wrapper>
    );

    // ══════════════════════════════════════════════════════════════════
    // PLAYING SCREEN
    // ══════════════════════════════════════════════════════════════════
    if (phase === 'playing') {
        const q = questions[currentIdx];
        const lastResult = results[results.length - 1];
        const aFb: FeedbackType = feedback && lastResult?.chosen === 'A' ? feedback : null;
        const bFb: FeedbackType = feedback && lastResult?.chosen === 'B' ? feedback : null;
        const holdZone = holdInfo?.zone ?? null;

        return (
            <Wrapper style={{ overflow: 'hidden' }}>
                <FbOverlay type={feedback}>{feedback === 'correct' ? '✅' : feedback === 'wrong' ? '❌' : ''}</FbOverlay>
                <GameOuter>
                    {/* Full-width question banner */}
                    <QuestionBanner>
                        <div style={{ fontSize: '.85rem', color: 'rgba(255,255,255,.4)', whiteSpace: 'nowrap' }}>
                            Câu {currentIdx + 1}/{questions.length}
                        </div>
                        <QBannerText>{q?.text}</QBannerText>
                        <TimerDisplay>
                            ⏱ {Math.floor(elapsed / 1000)}.{Math.floor((elapsed % 1000) / 100)}s
                        </TimerDisplay>
                    </QuestionBanner>

                    <GameLayout>
                        {/* LEFT — A */}
                        <AnswerZone side="A" active={activeZone === 'A'} fbType={aFb}>
                            <AnswerLabel side="A">A</AnswerLabel>
                            <AnswerText side="A">{q?.optionA}</AnswerText>
                            {q?.correct === 'A' && feedback === 'wrong' && <div style={{ marginTop: 16, color: '#86efac', fontSize: '1rem', fontWeight: 600 }}>← Đáp án đúng</div>}
                        </AnswerZone>

                        {/* CENTER */}
                        <CenterPanel>
                            <div style={{ width: '100%', height: 4, background: 'rgba(255,255,255,.1)', borderRadius: 2, marginBottom: 10 }}>
                                <div style={{ height: '100%', borderRadius: 2, background: '#818cf8', width: `${(currentIdx / questions.length) * 100}%`, transition: 'width .3s' }} />
                            </div>

                            <VideoBox>
                                <VideoEl ref={videoRef} autoPlay playsInline muted />
                                <CanvasEl ref={canvasRef} />
                                <ZoneLabel side="A" active={activeZone === 'A'}>A</ZoneLabel>
                                <ZoneLabel side="B" active={activeZone === 'B'}>B</ZoneLabel>
                                <HoldBarWrap>
                                    {holdZone && (
                                        <HoldBarFill key={holdInfo?.startedAt} side={holdZone} dur={HOLD_MS} active={true} />
                                    )}
                                </HoldBarWrap>
                            </VideoBox>

                            <StatusMsg>
                                {camError ? `⚠️ ${camError}` :
                                    activeZone ? `👋 Đang nhận vùng ${activeZone} — giữ thêm để chọn...` :
                                        '👀 Vẫy tay về vùng A (trái) hoặc B (phải)'}
                            </StatusMsg>
                            {/* Debug: motion meter */}
                            <div style={{ width: '100%', fontSize: '.7rem', color: 'rgba(255,255,255,.4)', marginBottom: 4 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#60a5fa' }}>A: {debugMotion.left}</span>
                                    <span style={{ fontSize: '.65rem', opacity: .6 }}>min={MOTION_MIN}</span>
                                    <span style={{ color: '#c084fc' }}>B: {debugMotion.right}</span>
                                </div>
                                <div style={{ display: 'flex', height: 3, gap: 2, marginTop: 2 }}>
                                    <div style={{ flex: 1, background: 'rgba(96,165,250,.2)', borderRadius: 2, overflow: 'hidden' }}>
                                        <div style={{ height: '100%', background: '#60a5fa', width: `${Math.min((debugMotion.left / Math.max(debugMotion.left, debugMotion.right, 1)) * 100, 100)}%`, transition: 'width .1s' }} />
                                    </div>
                                    <div style={{ flex: 1, background: 'rgba(192,132,252,.2)', borderRadius: 2, overflow: 'hidden' }}>
                                        <div style={{ height: '100%', background: '#c084fc', width: `${Math.min((debugMotion.right / Math.max(debugMotion.left, debugMotion.right, 1)) * 100, 100)}%`, transition: 'width .1s' }} />
                                    </div>
                                </div>
                            </div>
                            <ManualBtns>
                                <ManualBtn side="A" onClick={() => triggerSelect('A')}>🖐 Chọn A</ManualBtn>
                                <ManualBtn side="B" onClick={() => triggerSelect('B')}>🖐 Chọn B</ManualBtn>
                            </ManualBtns>
                            <EndBtn onClick={() => { stopCamera(); setPhase('input'); }}>⏹ Kết Thúc</EndBtn>
                        </CenterPanel>

                        {/* RIGHT — B */}
                        <AnswerZone side="B" active={activeZone === 'B'} fbType={bFb}>
                            <AnswerLabel side="B">B</AnswerLabel>
                            <AnswerText side="B">{q?.optionB}</AnswerText>
                            {q?.correct === 'B' && feedback === 'wrong' && <div style={{ marginTop: 16, color: '#86efac', fontSize: '1rem', fontWeight: 600 }}>← Đáp án đúng</div>}
                        </AnswerZone>
                    </GameLayout>
                </GameOuter>
            </Wrapper>
        );
    }

    // ══════════════════════════════════════════════════════════════════
    // RESULT SCREEN
    // ══════════════════════════════════════════════════════════════════
    const correct = results.filter(r => r.isCorrect).length;
    const pct = results.length ? Math.round((correct / results.length) * 100) : 0;
    const totalSec = Math.floor(totalTime / 1000);
    const totalMs = Math.floor((totalTime % 1000) / 100);
    return (
        <Wrapper>
            <ResultScreen>
                <ScoreCard>
                    <div style={{ fontSize: '3.5rem', marginBottom: 8 }}>{pct >= 80 ? '🎉' : pct >= 50 ? '👍' : '💪'}</div>
                    <h2 style={{ fontSize: '2.5rem', fontWeight: 900, background: 'linear-gradient(90deg,#60a5fa,#a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        {correct}/{results.length} câu đúng — {pct}%
                    </h2>
                    <div style={{ fontSize: '1.2rem', color: '#a78bfa', marginTop: 8 }}>
                        ⏱ Thời gian: <strong>{totalSec}.{totalMs}s</strong>
                    </div>
                </ScoreCard>
                {results.map((r, i) => (
                    <ResultItem key={i} correct={r.isCorrect}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 700, marginBottom: 5 }}>{r.isCorrect ? '✅' : '❌'} Câu {i + 1}: {r.question.text}</div>
                                <div style={{ fontSize: '.82rem', color: 'rgba(255,255,255,.65)' }}>
                                    <span style={{ marginRight: 14 }}>A. {r.question.optionA}</span><span>B. {r.question.optionB}</span>
                                </div>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <div style={{ fontSize: '.8rem', color: 'rgba(255,255,255,.45)' }}>Đã chọn</div>
                                <div style={{ fontWeight: 700, color: r.isCorrect ? '#86efac' : '#fca5a5', fontSize: '1.1rem' }}>{r.chosen}</div>
                                {!r.isCorrect && <div style={{ fontSize: '.75rem', color: '#86efac' }}>Đúng: {r.question.correct}</div>}
                            </div>
                        </div>
                    </ResultItem>
                ))}
                <div style={{ textAlign: 'center' }}>
                    <BackBtn onClick={() => { setPhase('input'); setResults([]); setCurrentIdx(0); }}>🔄 Chơi Lại</BackBtn>
                </div>
            </ResultScreen>
        </Wrapper>
    );
};

export default ClapGame;
